import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, CaretDown, CaretUp, Plus, X, FilePdf } from "@phosphor-icons/react";
import { PdfCanvasViewer } from "../PdfCanvasViewer";

const inputCls = "input-base w-full mt-0.5";

const AutoTextarea = ({ value, onChange, onBlur, autoFocus, testId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + 2}px`; }
  }, [value]);
  useEffect(() => { if (autoFocus && ref.current) ref.current.focus(); }, [autoFocus]);
  return (
    <textarea ref={ref} data-testid={testId} rows={1} value={value} onChange={onChange} onBlur={onBlur}
      className="input-base w-full text-xs leading-snug resize-none overflow-hidden" />
  );
};

const EditableCell = ({ value, onChange, isOutcome, testId }) => {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return <AutoTextarea autoFocus testId={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={() => setEditing(false)} />;
  }
  const v = value || "";
  const nl = v.indexOf("\n");
  return (
    <div data-testid={testId} onClick={() => setEditing(true)} title="Click to edit"
      className="w-full min-h-[30px] rounded-md border border-transparent hover:border-[#C4C8CE] hover:bg-[#FAFAF7] px-2 py-1.5 cursor-text text-black">
      {isOutcome && nl > -1 ? (
        <>
          <div className="text-[11px] italic leading-snug whitespace-pre-wrap">{v.slice(0, nl)}</div>
          <div className="text-sm leading-snug mt-1 whitespace-pre-wrap">{v.slice(nl + 1)}</div>
        </>
      ) : (
        <div className="text-xs leading-snug whitespace-pre-wrap">{v || <span className="text-[#A1A1AA]">Click to add</span>}</div>
      )}
    </div>
  );
};

export const CurriculumAlignmentCard = () => {
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewPdf, setViewPdf] = useState(null);

  useEffect(() => {
    if (open && !doc) api.get("/admin/site/curriculum-alignment").then(({ data }) => setDoc(data)).catch(() => {});
  }, [open, doc]);

  const save = async (patch = {}, label = "Saved") => {
    setBusy(true);
    const next = { ...doc, ...patch };
    try {
      await api.put("/admin/site/curriculum-alignment", next);
      setDoc(next);
      toast.success(label);
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
    finally { setBusy(false); }
  };

  const previewPdf = async () => {
    if (!(await save({}, "Table saved"))) return;
    try {
      const { data } = await api.get("/admin/site/curriculum-alignment.pdf", { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      setViewPdf({ blob, url: URL.createObjectURL(blob) });
    } catch { toast.error("Could not build the PDF preview"); }
  };

  const closeViewPdf = () => {
    if (viewPdf?.url) URL.revokeObjectURL(viewPdf.url);
    setViewPdf(null);
  };

  const printViewPdf = () => {
    if (!viewPdf?.url) return;
    const f = document.createElement("iframe");
    f.style.display = "none";
    f.src = viewPdf.url;
    f.onload = () => { try { f.contentWindow.print(); } catch { window.open(viewPdf.url, "_blank"); } };
    document.body.appendChild(f);
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Replace the whole table with the latest built-in version (word-for-word Alberta outcomes)? Your current edits will be overwritten.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/site/curriculum-alignment/reset");
      setDoc(data);
      toast.success("Loaded the latest built-in table");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const mergeBuiltin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/site/curriculum-alignment/merge-builtin");
      if (data.added?.length) {
        setDoc(data.doc);
        toast.success(`Added ${data.added.length} new section${data.added.length > 1 ? "s" : ""}: ${data.added.join("; ")} — all your edits kept`);
      } else {
        toast.success("Your table already has every built-in section — nothing to add");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setSec = (i, patch) => setDoc((d) => ({ ...d, sections: d.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const setCell = (i, r, c, v) => setSec(i, { rows: doc.sections[i].rows.map((row, j) => (j === r ? row.map((x, k) => (k === c ? v : x)) : row)) });
  const addRow = (i) => setSec(i, { rows: [...doc.sections[i].rows, doc.sections[i].columns.map(() => "")] });
  const delRow = (i, r) => setSec(i, { rows: doc.sections[i].rows.filter((_, j) => j !== r) });
  const addCol = (i) => {
    const s = doc.sections[i];
    if (s.columns.length >= 6) return;
    setSec(i, { columns: [...s.columns, `Column ${s.columns.length + 1}`], rows: s.rows.map((row) => [...row, ""]) });
  };
  const delCol = (i, c) => {
    const s = doc.sections[i];
    if (s.columns.length <= 1) return;
    setSec(i, { columns: s.columns.filter((_, k) => k !== c), rows: s.rows.map((row) => row.filter((_, k) => k !== c)) });
  };
  const addSection = () => setDoc((d) => ({
    ...d,
    sections: [...d.sections, { heading: "New section", columns: ["Curriculum requirement (paraphrased)", "How the Self-Agency course addresses it"], rows: [["", ""]] }],
  }));
  const delSection = (i) => setDoc((d) => ({ ...d, sections: d.sections.filter((_, j) => j !== i) }));

  return (
    <div className="rounded-xl border border-[#C4C8CE] bg-white p-3 mb-4" data-testid="curriculum-alignment-card">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between" data-testid="curriculum-toggle">
        <span className="flex items-center gap-2 font-display font-bold text-sm">
          <GraduationCap size={17} weight="duotone" color="#84CC16" /> Curriculum alignment (PEW · CALM · CEFL)
          {doc?.issued && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#84CC16]/15 text-[#4D7C0F]" data-testid="curriculum-issued-badge">ISSUED</span>}
        </span>
        {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </button>
      {open && (
        <div className="mt-3">
          <p className="text-xs text-[#52525B] mb-3 m-0">
            Edit the alignment table, preview it, then <b>Issue</b> to publish the PDF download on selfagency.ca/schools.
            While issued, saved edits update the public PDF immediately. <b>Your saved edits are permanent</b> — reloads,
            reopens and app updates never overwrite them. When a new app build ships updated built-in content, it
            <b> merges into your saved table automatically</b>: blank or untouched cells get the new text, and any cell
            you've edited always keeps your version. The "Load latest built-in table" button below is the only thing
            that replaces your copy, and only when you click it and confirm.
          </p>
          {!doc ? <div className="text-sm text-[#71717A]">Loading…</div> : (
            <div className="flex flex-col gap-3">
              <label className="text-xs text-[#52525B]">Title
                <input data-testid="curriculum-title" className={inputCls} value={doc.title || ""} onChange={(e) => setDoc((d) => ({ ...d, title: e.target.value }))} />
              </label>
              <label className="text-xs text-[#52525B]">Intro (shown under the title)
                <AutoTextarea testId="curriculum-intro" value={doc.intro || ""} onChange={(e) => setDoc((d) => ({ ...d, intro: e.target.value }))} />
              </label>

              {doc.sections.map((s, i) => (
                <div key={i} className="border border-[#E4E4E7] rounded-lg p-2.5" data-testid={`curriculum-section-${i}`}>
                  <div className="flex items-center gap-2">
                    <input data-testid={`curriculum-section-heading-${i}`} className="input-base flex-1 font-semibold" value={s.heading} placeholder="Section heading"
                      onChange={(e) => setSec(i, { heading: e.target.value })} />
                    <button onClick={() => delSection(i)} data-testid={`curriculum-del-section-${i}`} title="Remove section"
                      className="text-[#A1A1AA] hover:text-red-500 bg-transparent border-0 cursor-pointer p-1"><X size={14} /></button>
                  </div>
                  <div className="overflow-x-auto mt-2">
                    <table className="w-full border-collapse" style={{ minWidth: s.columns.length * 220 }}>
                      <thead>
                        <tr>
                          {s.columns.map((c, ci) => (
                            <th key={ci} className="p-1 align-top">
                              <div className="flex items-center gap-1">
                                <input data-testid={`curriculum-col-${i}-${ci}`} className="input-base w-full text-xs font-bold" value={c}
                                  onChange={(e) => setSec(i, { columns: s.columns.map((x, k) => (k === ci ? e.target.value : x)) })} />
                                {s.columns.length > 1 && (
                                  <button onClick={() => delCol(i, ci)} title="Remove column" data-testid={`curriculum-del-col-${i}-${ci}`}
                                    className="text-[#A1A1AA] hover:text-red-500 bg-transparent border-0 cursor-pointer p-0.5"><X size={12} /></button>
                                )}
                              </div>
                            </th>
                          ))}
                          <th className="w-6" />
                        </tr>
                      </thead>
                      <tbody>
                        {s.rows.map((row, ri) => (
                          <tr key={ri}>
                            {s.columns.map((_, ci) => (
                              <td key={ci} className="p-1 align-top">
                                <EditableCell testId={`curriculum-cell-${i}-${ri}-${ci}`} isOutcome={ci === 0}
                                  value={row[ci] || ""} onChange={(v) => setCell(i, ri, ci, v)} />
                              </td>
                            ))}
                            <td className="align-top pt-2">
                              <button onClick={() => delRow(i, ri)} title="Remove row" data-testid={`curriculum-del-row-${i}-${ri}`}
                                className="text-[#A1A1AA] hover:text-red-500 bg-transparent border-0 cursor-pointer p-0.5"><X size={12} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-2 mt-1.5">
                    <button onClick={() => addRow(i)} data-testid={`curriculum-add-row-${i}`}
                      className="text-xs font-bold text-[#4D7C0F] bg-transparent border-0 cursor-pointer inline-flex items-center gap-1"><Plus size={12} /> Row</button>
                    <button onClick={() => addCol(i)} data-testid={`curriculum-add-col-${i}`}
                      className="text-xs font-bold text-[#4D7C0F] bg-transparent border-0 cursor-pointer inline-flex items-center gap-1"><Plus size={12} /> Column</button>
                  </div>
                </div>
              ))}
              <button onClick={addSection} data-testid="curriculum-add-section"
                className="text-xs font-bold text-[#4D7C0F] bg-transparent border border-dashed border-[#C4C8CE] rounded-lg py-2 cursor-pointer inline-flex items-center justify-center gap-1">
                <Plus size={13} /> Add section
              </button>

              <label className="text-xs text-[#52525B]">Notes for reviewers (one per line)
                <AutoTextarea testId="curriculum-notes"
                  value={(doc.notes || []).join("\n")}
                  onChange={(e) => setDoc((d) => ({ ...d, notes: e.target.value.split("\n") }))} />
              </label>
              <label className="text-xs text-[#52525B]">Contact line (PDF footer)
                <input data-testid="curriculum-contact" className={inputCls} value={doc.contact || ""} onChange={(e) => setDoc((d) => ({ ...d, contact: e.target.value }))} />
              </label>

              <div className="flex flex-wrap gap-2 mt-1">
                <button data-testid="curriculum-save" onClick={() => save({}, "Table saved — your edits are stored and will be here when you come back")} disabled={busy} className="btn-primary flex-1 disabled:opacity-50">
                  {busy ? "Working…" : "Save table"}
                </button>
                <button data-testid="curriculum-preview" onClick={previewPdf} disabled={busy}
                  className="flex-1 rounded-lg border border-[#C4C8CE] bg-white font-bold text-sm py-2 cursor-pointer inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                  <FilePdf size={15} /> Preview PDF
                </button>
                {doc.issued ? (
                  <button data-testid="curriculum-unpublish" onClick={() => save({ issued: false }, "PDF removed from the schools page")} disabled={busy}
                    className="flex-1 rounded-lg border border-red-300 text-red-600 bg-white font-bold text-sm py-2 cursor-pointer disabled:opacity-50">
                    Unpublish
                  </button>
                ) : (
                  <button data-testid="curriculum-issue" onClick={() => save({ issued: true }, "Issued — PDF is now live on /schools")} disabled={busy}
                    className="flex-1 rounded-lg border-0 bg-[#84CC16] text-black font-bold text-sm py-2 cursor-pointer disabled:opacity-50">
                    Issue as PDF
                  </button>
                )}
              </div>
              <button data-testid="curriculum-merge-builtin" onClick={mergeBuiltin} disabled={busy}
                className="text-xs text-[#4D7C0F] bg-transparent border-0 cursor-pointer underline self-start disabled:opacity-50">
                Add new built-in sections (adds anything shipped since your last save — keeps all your edits)
              </button>
              <button data-testid="curriculum-reset" onClick={resetToDefaults} disabled={busy}
                className="text-xs text-[#71717A] bg-transparent border-0 cursor-pointer underline self-start disabled:opacity-50">
                Load latest built-in table (word-for-word Alberta outcomes — replaces current edits)
              </button>
            </div>
          )}
        </div>
      )}
      {viewPdf && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="curriculum-pdf-modal" onClick={closeViewPdf}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl flex flex-col" style={{ height: "90vh" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#E4E4E7]">
              <div className="font-bold text-sm">Curriculum Alignment — PDF preview</div>
              <div className="flex items-center gap-2">
                <button onClick={printViewPdf} className="btn-secondary text-xs" data-testid="curriculum-pdf-print">Print</button>
                <a href={viewPdf.url} download="self-agency-curriculum-alignment.pdf" className="btn-secondary text-xs" data-testid="curriculum-pdf-download">Download</a>
                <button data-testid="curriculum-pdf-close" className="btn-ghost" onClick={closeViewPdf} title="Close"><X size={18} weight="bold" /></button>
              </div>
            </div>
            <PdfCanvasViewer blob={viewPdf.blob} url={viewPdf.url} />
          </div>
        </div>
      )}
    </div>
  );
};
